import { apiClient } from './client';
import {
  VisitSource,
  type ProfileVisit,
  type ProfileVisitActor,
  type ProfileVisitStats,
  type VisitSourceValue,
} from '@/types/profile-visit';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isVisitSource(value: unknown): value is VisitSourceValue {
  return typeof value === 'string' && Object.values(VisitSource).some((source) => source === value);
}

function isSafeMediaUrl(value: unknown): value is string {
  return typeof value === 'string' && (value.startsWith('/') || /^https?:\/\//i.test(value));
}

function parseActor(value: unknown, index: number): ProfileVisitActor {
  if (
    !isRecord(value) ||
    (value.kind !== 'character' && value.kind !== 'user') ||
    typeof value.display_name !== 'string' ||
    value.display_name.trim().length === 0 ||
    (value.avatar_url !== null && value.avatar_url !== undefined && !isSafeMediaUrl(value.avatar_url)) ||
    (value.target_path !== null && value.target_path !== undefined && typeof value.target_path !== 'string')
  ) {
    throw new TypeError(`Invalid profile visit actor at index ${index}`);
  }

  const targetPath = value.target_path;
  if (
    (value.kind === 'user' && targetPath !== null && targetPath !== undefined) ||
    (targetPath !== null && targetPath !== undefined && !/^\/characters\/[1-9]\d*$/.test(targetPath))
  ) {
    throw new TypeError(`Unsafe profile visit target at index ${index}`);
  }

  return {
    kind: value.kind,
    display_name: value.display_name.trim(),
    ...(value.avatar_url !== null && value.avatar_url !== undefined ? { avatar_url: value.avatar_url } : {}),
    ...(targetPath !== null && targetPath !== undefined ? { target_path: targetPath as `/characters/${number}` } : {}),
  };
}

function parseProfileVisit(value: unknown, index: number): ProfileVisit {
  if (
    !isRecord(value) ||
    !isPositiveInteger(value.id) ||
    (value.character_id !== null && value.character_id !== undefined && !isPositiveInteger(value.character_id)) ||
    (value.visit_duration !== null &&
      value.visit_duration !== undefined &&
      (typeof value.visit_duration !== 'number' ||
        !Number.isFinite(value.visit_duration) ||
        value.visit_duration < 0)) ||
    (value.visit_source !== null && value.visit_source !== undefined && !isVisitSource(value.visit_source)) ||
    typeof value.triggered_message !== 'boolean' ||
    typeof value.visit_score !== 'number' ||
    !Number.isFinite(value.visit_score) ||
    typeof value.created_at !== 'string' ||
    Number.isNaN(Date.parse(value.created_at))
  ) {
    throw new TypeError(`Invalid profile visit response at index ${index}`);
  }

  return {
    id: value.id,
    triggered_message: value.triggered_message,
    visit_score: value.visit_score,
    created_at: value.created_at,
    actor: parseActor(value.actor, index),
    ...(value.character_id !== null && value.character_id !== undefined ? { character_id: value.character_id } : {}),
    ...(value.visit_duration !== null && value.visit_duration !== undefined
      ? { visit_duration: value.visit_duration }
      : {}),
    ...(value.visit_source !== null && value.visit_source !== undefined ? { visit_source: value.visit_source } : {}),
  };
}

export function parseProfileVisitsResponse(value: unknown): ProfileVisit[] {
  if (!Array.isArray(value)) {
    throw new TypeError('Invalid profile visits response: expected an array');
  }
  return value.map(parseProfileVisit);
}

export function parseProfileVisitSocketData(value: unknown): ProfileVisit {
  if (
    !isRecord(value) ||
    !isPositiveInteger(value.visit_id) ||
    !isPositiveInteger(value.character_id) ||
    typeof value.character_name !== 'string' ||
    value.character_name.trim().length === 0 ||
    (value.character_image !== null && value.character_image !== undefined && !isSafeMediaUrl(value.character_image)) ||
    (value.visit_duration !== null &&
      value.visit_duration !== undefined &&
      (typeof value.visit_duration !== 'number' ||
        !Number.isFinite(value.visit_duration) ||
        value.visit_duration < 0)) ||
    (value.visit_source !== null && value.visit_source !== undefined && !isVisitSource(value.visit_source)) ||
    typeof value.timestamp !== 'string' ||
    Number.isNaN(Date.parse(value.timestamp))
  ) {
    throw new TypeError('Invalid profile visit WebSocket payload');
  }

  return {
    id: value.visit_id,
    character_id: value.character_id,
    triggered_message: value.triggered_message === true,
    visit_score: 0,
    created_at: value.timestamp,
    actor: {
      kind: 'character',
      display_name: value.character_name.trim(),
      target_path: `/characters/${value.character_id}`,
      ...(value.character_image !== null && value.character_image !== undefined
        ? { avatar_url: value.character_image }
        : {}),
    },
    ...(value.visit_duration !== null && value.visit_duration !== undefined
      ? { visit_duration: value.visit_duration }
      : {}),
    ...(value.visit_source !== null && value.visit_source !== undefined ? { visit_source: value.visit_source } : {}),
  };
}

function parseMarkSeenResponse(value: unknown): { success: boolean; marked: number } {
  if (
    !isRecord(value) ||
    value.success !== true ||
    typeof value.marked !== 'number' ||
    !Number.isSafeInteger(value.marked) ||
    value.marked < 0
  ) {
    throw new TypeError('Invalid mark-profile-visits-seen response');
  }
  return { success: true, marked: value.marked };
}

export const profileVisitService = {
  async getProfileVisits(params?: {
    limit?: number;
    offset?: number;
    include_seen?: boolean;
  }): Promise<ProfileVisit[]> {
    const { data } = await apiClient.get<unknown>('/profile-visits/', { params });
    return parseProfileVisitsResponse(data);
  },

  async markAsSeen(visitIds: number[]): Promise<{ success: boolean; marked: number }> {
    const { data } = await apiClient.post<unknown>('/profile-visits/mark-seen', visitIds);
    return parseMarkSeenResponse(data);
  },

  async simulateVisits(): Promise<ProfileVisit[]> {
    const { data } = await apiClient.post<ProfileVisit[]>('/profile-visits/simulate');
    return data;
  },

  async trackVisit(params: {
    visited_user_id: number;
    visit_duration?: number;
    visit_source?: VisitSourceValue;
  }): Promise<ProfileVisit> {
    const { data } = await apiClient.post<ProfileVisit>('/profile-visits/track', null, { params });
    return data;
  },

  async getStats(days?: number): Promise<ProfileVisitStats> {
    const params = days ? { days } : undefined;
    const { data } = await apiClient.get<ProfileVisitStats>('/profile-visits/stats', { params });
    return data;
  },

  async trackCharacterVisit(params: {
    character_id: number;
    visit_duration?: number;
    visit_source?: VisitSourceValue;
  }): Promise<ProfileVisit> {
    const { data } = await apiClient.post<ProfileVisit>('/profile-visits/track-character', null, { params });
    return data;
  },

  // Admin endpoints
  async getSettings(): Promise<unknown> {
    const { data } = await apiClient.get<unknown>('/admin/profile-visits/settings');
    return data;
  },

  async updateSettings(settings: Record<string, unknown>): Promise<unknown> {
    const { data } = await apiClient.put<unknown>('/admin/profile-visits/settings', settings);
    return data;
  },

  async adminSimulateVisits(userId?: number): Promise<unknown> {
    const params = userId ? { user_id: userId } : undefined;
    const { data } = await apiClient.post<unknown>('/admin/profile-visits/simulate', null, { params });
    return data;
  },

  async getAdminStats(days = 7): Promise<unknown> {
    const { data } = await apiClient.get<unknown>('/admin/profile-visits/stats', { params: { days } });
    return data;
  },

  async getRecentVisits(params?: {
    limit?: number;
    offset?: number;
    user_id?: number;
    character_id?: number;
  }): Promise<unknown[]> {
    const { data } = await apiClient.get<unknown[]>('/admin/profile-visits/recent', { params });
    return data;
  },
};
